import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { salesAPI, productsAPI, ordersAPI } from '../../services/api';
import { useCompany } from '../CompanyContext';
import { useUser } from '../UserContext';
import { Sale, CreateSaleFromOrderDto, UpdateSaleDto, SaleStatus, PaymentMethod, Product, Order } from '../../types';
import {
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { toastSuccess } from '../../utils/toast';

// Ensure this file is treated as a module under isolatedModules
export {};

const SaleForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('orderId');

  // Redirect if trying to edit an existing sale - sales are read-only
  useEffect(() => {
    if (id) {
      console.warn('Attempting to edit sale - redirecting to view');
      navigate(`/sales/${id}`);
      return;
    }
  }, [id, navigate]);
  const { selectedCompany } = useCompany();
  const { currentUser } = useUser();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [sourceOrder, setSourceOrder] = useState<Order | null>(null);

  const [formData, setFormData] = useState<CreateSaleFromOrderDto>({
    notes: '',
    tax: 0,
    discount: 0,
    paymentMethod: PaymentMethod.CASH,
    items: [],
  });

  const [newItem, setNewItem] = useState({
    productId: '',
    quantity: 1,
    unitPrice: 0,
    discount: 0,
  });

  useEffect(() => {
    if (selectedCompany && !id) { // Only load if not trying to edit
      loadProducts();
      if (orderId) {
        loadSourceOrder();
      }
    }
  }, [selectedCompany, id, orderId]);

  const loadSourceOrder = async () => {
    if (!orderId) return;

    try {
      const order = await ordersAPI.getById(orderId);
      if (order.status === 'completed') {
        setSourceOrder(order);
        // Pre-populate sale data from order
        setFormData(prev => ({
          ...prev,
          notes: order.notes || '',
          tax: order.tax || 0,
          discount: order.discount || 0,
          items: order.items?.map(item => ({
            productId: item.productId,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            discount: item.discount || 0,
          })) || [],
        }));
      } else {
        setError('Order must be completed before generating a sale');
      }
    } catch (err) {
      setError('Failed to load source order');
      console.error('Error loading source order:', err);
    }
  };

  // Removed loadFormData function - sales cannot be edited

  const loadProducts = async () => {
    try {
      const productsData = await productsAPI.getByCompany(selectedCompany!.id);
      setProducts(productsData);
    } catch (err) {
      console.error('Error loading products:', err);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value,
    }));
  };

  const handleAddItem = () => {
    if (!newItem.productId || newItem.quantity <= 0) return;

    const product = products.find(p => p.id === newItem.productId);
    if (!product) return;

    const item = {
      productId: newItem.productId,
      quantity: newItem.quantity,
      unitPrice: product.price,
      discount: newItem.discount,
    };

    setFormData(prev => ({
      ...prev,
      items: [...prev.items, item],
    }));

    // Reset new item form
    setNewItem({
      productId: '',
      quantity: 1,
      unitPrice: 0,
      discount: 0,
    });
  };

  const handleRemoveItem = (index: number) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      items: newItems,
    }));
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => {
      const itemTotal = (item.unitPrice * item.quantity) - (item.discount || 0);
      return sum + itemTotal;
    }, 0);

    const tax = subtotal * 0.1; // 10% tax
    const total = subtotal + tax;

    return { subtotal, tax, total };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      // Only allow creating sales from orders - no editing allowed
      if (!orderId) {
        setError('Order ID is required to create a sale');
        return;
      }
      await salesAPI.createFromOrder(orderId, formData);
      toastSuccess('Sale created successfully');
      navigate('/sales');
    } catch (err) {
      setError('Failed to create sale');
      console.error('Error creating sale:', err);
    } finally {
      setSaving(false);
    }
  };

  if (!selectedCompany) {
    return (
      <div className="text-center py-12">
        <h3 className="mt-2 text-sm font-medium text-gray-900">No company selected</h3>
        <p className="mt-1 text-sm text-gray-500">Please select a company to create a sale.</p>
      </div>
    );
  }

  if (!orderId) {
    return (
      <div className="text-center py-12">
        <h3 className="mt-2 text-sm font-medium text-gray-900">Order Required</h3>
        <p className="mt-1 text-sm text-gray-500">Sales can only be created from completed orders and cannot be edited once created.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const { subtotal, tax, total } = calculateTotals();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/sales')}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-all duration-200"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Generate Sale from Order
            </h1>
            <p className="text-gray-600">Sale for {selectedCompany.name}</p>
            {orderId && sourceOrder && (
              <p className="text-sm text-gray-500 mt-1">
                Based on Order #{sourceOrder.id.slice(0, 8)} - {sourceOrder.customerName}
              </p>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">{error}</div>
        </div>
      )}

      {/* Source Order Information */}
      {orderId && sourceOrder && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-blue-900 mb-4 flex items-center">
            <CheckIcon className="h-5 w-5 mr-2" />
            Source Order Information
          </h3>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div>
              <h4 className="font-medium text-blue-800 mb-2">Order Details</h4>
              <p className="text-blue-700 text-sm">Order ID: {sourceOrder.id.slice(0, 8)}...</p>
              <p className="text-blue-700 text-sm">Status: {sourceOrder.status}</p>
              <p className="text-blue-700 text-sm">Type: {sourceOrder.type}</p>
            </div>
            <div>
              <h4 className="font-medium text-blue-800 mb-2">Customer</h4>
              <p className="text-blue-700 text-sm">{sourceOrder.customerName}</p>
              <p className="text-blue-700 text-sm">{sourceOrder.customerEmail}</p>
              <p className="text-blue-700 text-sm">{sourceOrder.customerPhone}</p>
            </div>
            <div>
              <h4 className="font-medium text-blue-800 mb-2">Order Totals</h4>
              <p className="text-blue-700 text-sm">Subtotal: ${sourceOrder.subtotal}</p>
              <p className="text-blue-700 text-sm">Tax: ${sourceOrder.tax}</p>
              <p className="text-blue-700 text-sm">Total: ${sourceOrder.totalAmount}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Sale Details */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Sale Details</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Payment Method</label>
                <select
                  name="paymentMethod"
                  value={formData.paymentMethod}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value={PaymentMethod.CASH}>Cash</option>
                  <option value={PaymentMethod.CREDIT_CARD}>Credit Card</option>
                  <option value={PaymentMethod.DEBIT_CARD}>Debit Card</option>
                  <option value={PaymentMethod.BANK_TRANSFER}>Bank Transfer</option>
                  <option value={PaymentMethod.DIGITAL_WALLET}>Digital Wallet</option>
                </select>
              </div>


            </div>
          </div>

          {/* Sale Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Sale Information</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Tax</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  name="tax"
                  value={formData.tax}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Discount</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  name="discount"
                  value={formData.discount}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={3}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Sale Items */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Sale Items</h3>
          
          {/* Add New Item - Only show if not from order */}
          {!orderId && (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-5 mb-6 p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700">Product</label>
                <select
                  value={newItem.productId}
                  onChange={(e) => setNewItem(prev => ({ ...prev, productId: e.target.value }))}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select Product</option>
                  {products.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.name} - ${product.price}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Quantity</label>
                <input
                  type="number"
                  min="1"
                  value={newItem.quantity}
                  onChange={(e) => setNewItem(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Unit Price</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newItem.unitPrice}
                  onChange={(e) => setNewItem(prev => ({ ...prev, unitPrice: parseFloat(e.target.value) || 0 }))}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Discount</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newItem.discount}
                  onChange={(e) => setNewItem(prev => ({ ...prev, discount: parseFloat(e.target.value) || 0 }))}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div className="flex items-end">
                <button
                  type="button"
                  onClick={handleAddItem}
                  className="w-full bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <PlusIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* Items List */}
          {formData.items.length > 0 ? (
            <div className="space-y-2">
              {formData.items.map((item, index) => {
                const product = products.find(p => p.id === item.productId);
                const itemTotal = (item.unitPrice * item.quantity) - (item.discount || 0);
                
                return (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium">{product?.name || 'Unknown Product'}</p>
                      <p className="text-sm text-gray-500">
                        Qty: {item.quantity} × ${item.unitPrice} - ${item.discount || 0} discount
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium">${itemTotal.toFixed(2)}</span>
                      {!orderId && (
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No items added to this sale.</p>
          )}
        </div>

        {/* Sale Summary */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Sale Summary</h3>
          
          <div className="space-y-2">
            <div className="flex justify-between">
              <span>Subtotal:</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Tax (10%):</span>
              <span>${tax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-bold text-lg border-t pt-2">
              <span>Total:</span>
              <span>${total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/sales')}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {saving ? 'Generating...' : 'Generate Sale'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SaleForm;
